import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Phone, Mail, ArrowRight, CheckCircle2 } from 'lucide-react'
import { fetchPageBySlug } from '../lib/cms'
import useMeta from '../hooks/useMeta'

export default function JegTilbyder() {
  const [pageData, setPageData] = useState(null)
  const [certifikatData, setCertifikatData] = useState(null)
  const [fjernvarmeData, setFjernvarmeData] = useState(null)
  const [muffeData, setMuffeData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function loadData() {
      const [main, cert, fjern, muffe] = await Promise.all([
        fetchPageBySlug('jeg-tilbyder'),
        fetchPageBySlug('certifikatsvejsning'),
        fetchPageBySlug('fjernvarme-svejsning-montering'),
        fetchPageBySlug('muffemontering')
      ])
      setPageData(main)
      setCertifikatData(cert)
      setFjernvarmeData(fjern)
      setMuffeData(muffe)
      setLoading(false)
    }
    loadData()
  }, [])

  // Set metadata
  useMeta({
    title: 'Jeg tilbyder · JP Stål og Rør ApS',
    description: 'Se hvad JP Stål og Rør ApS tilbyder af erhvervsløsninger inden for certifikatsvejsning, fjernvarmesvejsning og muffemontering.',
    ogTitle: 'Jeg tilbyder · JP Stål og Rør ApS',
    ogDescription: 'Se hvad JP Stål og Rør ApS tilbyder af erhvervsløsninger inden for certifikatsvejsning, fjernvarmesvejsning og muffemontering.',
    ogImage: 'https://assets.auxodev.dk/assets/e3c28b93-c07f-41a2-89fa-cab890739d18/68324731-ed12-4ac9-a74f-439c7a30c8ed.jpg',
    canonicalUrl: 'https://jpstaalogroer.dk/jeg-tilbyder'
  })

  if (loading) {
    return (
      <div className="min-h-screen bg-silver-200 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500"></div>
      </div>
    )
  }

  return (
    <div className="bg-silver-200 text-slate-900 min-h-screen py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Page Header */}
        <div className="text-center max-w-3xl mx-auto mb-20">
          <span className="text-xs font-bold uppercase tracking-widest text-amber-600">Ydelser til erhverv</span>
          <h1 className="text-4xl sm:text-5xl font-black text-slate-950 uppercase tracking-tight mt-2 mb-6">
            Professionelle stål- og rørløsninger
          </h1>
          <p className="text-slate-700 text-lg leading-relaxed">
            JP Stål og Rør ApS leverer kvalitetsarbejde inden for certifikatsvejsning, fjernvarmesvejsning, fjernvarmemontering og muffemontering. Vi arbejder udelukkende med erhvervskunder, industri, entreprenører og professionelle samarbejdspartnere.
          </p>
        </div>

        {/* Services List */}
        <div className="space-y-24 md:space-y-32">
          
          {/* Service 1: Certifikatsvejsning */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 space-y-6">
              <span className="text-xs font-bold uppercase tracking-wider text-amber-700 bg-amber-500/20 px-3 py-1 rounded-sm border border-amber-500/30">
                Ydelse 01
              </span>
              <h2 className="text-3xl font-black text-slate-950 uppercase tracking-tight">
                Certifikatsvejsning
              </h2>
              <h3 className="text-lg font-bold text-slate-800">
                Jeg laver certifikatsvejsning
              </h3>
              <div className="text-slate-700 text-sm leading-relaxed space-y-4 font-normal">
                <p>
                  Jeg tilbyder certifikatsvejsning til de højeste krav såsom til rustfrit stål, fødevarer industrien og Pharma industrien hvor der oftest er efterspørgsel på TIG-svejsning.
                </p>
                <p>
                  Fordelene ved Tig-svejning er at svejsestrengen ikke kræver yderligere rengøring.
                </p>
                <p>
                  Kontakt mig i dag, for at høre, hvordan jeg kan hjælpe med certifikat svejsning.
                </p>
              </div>
              
              {/* Heavy CTA Buttons */}
              <div className="pt-4 flex flex-wrap gap-4">
                <a
                  href="tel:+4524460485"
                  className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-slate-950 font-black uppercase tracking-wider text-xs px-6 py-4 rounded-sm transition-colors duration-200 cursor-pointer shadow-md"
                >
                  <Phone className="w-4 h-4" />
                  <span>Ring: (+45) 24 46 04 85</span>
                </a>
                <a
                  href="mailto:jp@jpstaal.dk"
                  className="inline-flex items-center gap-2 bg-silver-300 hover:bg-silver-400 text-slate-950 border border-silver-400 font-bold uppercase tracking-wider text-xs px-6 py-4 rounded-sm transition-colors duration-200 cursor-pointer shadow-sm"
                >
                  <Mail className="w-4 h-4 text-amber-600" />
                  <span>Mail: jp@jpstaal.dk</span>
                </a>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="aspect-[4/3] bg-silver-300 border border-silver-400 rounded-sm overflow-hidden shadow-xl">
                <img
                  src="https://assets.auxodev.dk/assets/e3c28b93-c07f-41a2-89fa-cab890739d18/68324731-ed12-4ac9-a74f-439c7a30c8ed.jpg"
                  alt="Certifikatsvejsning"
                  className="w-full h-full object-cover filter grayscale hover:grayscale-0 transition-all duration-500"
                />
              </div>
            </div>
          </div>

          {/* Service 2: Fjernvarme svejsning og montering */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center lg:flex-row-reverse">
            <div className="order-2 lg:col-span-5 lg:order-2">
              <div className="aspect-[4/3] bg-silver-300 border border-silver-400 rounded-sm overflow-hidden shadow-xl">
                <img
                  src="https://assets.auxodev.dk/assets/e3c28b93-c07f-41a2-89fa-cab890739d18/4528736c-cb84-4ce1-b39d-ed554f51130c.jpg"
                  alt="Fjernvarme svejsning og montering"
                  className="w-full h-full object-cover filter grayscale hover:grayscale-0 transition-all duration-500"
                />
              </div>
            </div>
            <div className="order-1 lg:col-span-7 lg:order-1 space-y-6">
              <span className="text-xs font-bold uppercase tracking-wider text-amber-700 bg-amber-500/20 px-3 py-1 rounded-sm border border-amber-500/30">
                Ydelse 02
              </span>
              <h2 className="text-3xl font-black text-slate-950 uppercase tracking-tight">
                Fjernvarme svejsning &amp; montering
              </h2>
              <h3 className="text-lg font-bold text-slate-800">
                Jeg tilbyder fjernvarme montering og fjernvarme svejsning​
              </h3>
              <div className="text-slate-700 text-sm leading-relaxed space-y-4 font-normal">
                <p>
                  Her tilbydes certifikat TIG-svejsning af fjernvarmerør til de højeste krav. TIG-svejsning bruges i mange industrier og applikationer, herunder til svejsning af fjernvarmerør.
                </p>
                <p>
                  Jeg monterer og installerer altid fjernvarmerør af højeste kvalitet. For mig er det vigtigt, at der efter levering ikke opstår problemer med fjernvarmerør og materialer som følge af enten dårlig kvalitet eller mangelfuld udførelse.​
                </p>
                <p>
                  Svejsningen sikrer, at rørledningerne er tæt forbundne og kan modstå det høje tryk og de temperaturvariationer, som fjernvarmesystemer ofte udsættes for. Korrekt montering minimerer også risikoen for driftsstop og dyre reparationer i fremtiden.
                </p>
                <div className="pt-2">
                  <p className="font-bold text-slate-950 mb-2 uppercase tracking-wider text-xs">Jeg udfører:</p>
                  <ul className="grid grid-cols-2 gap-2 text-xs text-slate-800 font-medium">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-amber-600 shrink-0" />
                      <span>Ny-anlæg</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-amber-600 shrink-0" />
                      <span>Renovering</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-amber-600 shrink-0" />
                      <span>Nyudstykning</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-amber-600 shrink-0" />
                      <span>Centralarbejde</span>
                    </li>
                  </ul>
                </div>
              </div>
              
              {/* Heavy CTA Buttons */}
              <div className="pt-4 flex flex-wrap gap-4">
                <a
                  href="tel:+4524460485"
                  className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-slate-950 font-black uppercase tracking-wider text-xs px-6 py-4 rounded-sm transition-colors duration-200 cursor-pointer shadow-md"
                >
                  <Phone className="w-4 h-4" />
                  <span>Ring: (+45) 24 46 04 85</span>
                </a>
                <a
                  href="mailto:jp@jpstaal.dk"
                  className="inline-flex items-center gap-2 bg-silver-300 hover:bg-silver-400 text-slate-950 border border-silver-400 font-bold uppercase tracking-wider text-xs px-6 py-4 rounded-sm transition-colors duration-200 cursor-pointer shadow-sm"
                >
                  <Mail className="w-4 h-4 text-amber-600" />
                  <span>Mail: jp@jpstaal.dk</span>
                </a>
              </div>
            </div>
          </div>

          {/* Service 3: Muffemontering */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 space-y-6">
              <span className="text-xs font-bold uppercase tracking-wider text-amber-700 bg-amber-500/20 px-3 py-1 rounded-sm border border-amber-500/30">
                Ydelse 03
              </span>
              <h2 className="text-3xl font-black text-slate-950 uppercase tracking-tight">
                Muffemontering
              </h2>
              <h3 className="text-lg font-bold text-slate-800">
                Jeg tilbyder muffemontering
              </h3>
              <div className="text-slate-700 text-sm leading-relaxed space-y-4 font-normal">
                <p>
                  Her er en samlet oversigt over mine certifikater:
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-silver-100 border border-silver-400 p-4 rounded-sm text-xs shadow-sm">
                  <div>
                    <p className="font-bold text-slate-950 mb-2 uppercase tracking-wider">Isoplus:</p>
                    <ul className="space-y-1 text-slate-700">
                      <li>• Krympemuffer</li>
                      <li>• Svejsemuffer</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-bold text-slate-950 mb-2 uppercase tracking-wider">Logstor:</p>
                    <ul className="space-y-1 text-slate-700">
                      <li>• EW- &amp; BandJoint</li>
                    </ul>
                  </div>
                </div>
                <div className="pt-2">
                  <p className="font-bold text-slate-950 mb-2 uppercase tracking-wider text-xs">Jeg arbejder også med:</p>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-slate-800 font-medium">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-amber-600 shrink-0" />
                      <span>Muffe- og afgreningsmontage</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-amber-600 shrink-0" />
                      <span>Opskumning af muffer</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-amber-600 shrink-0" />
                      <span>Montage og isolering af anboringer</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-amber-600 shrink-0" />
                      <span>Montage af alarmsystem</span>
                    </li>
                  </ul>
                </div>
              </div>
              
              {/* Heavy CTA Buttons */}
              <div className="pt-4 flex flex-wrap gap-4">
                <a
                  href="tel:+4524460485"
                  className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-slate-950 font-black uppercase tracking-wider text-xs px-6 py-4 rounded-sm transition-colors duration-200 cursor-pointer shadow-md"
                >
                  <Phone className="w-4 h-4" />
                  <span>Ring: (+45) 24 46 04 85</span>
                </a>
                <a
                  href="mailto:jp@jpstaal.dk"
                  className="inline-flex items-center gap-2 bg-silver-300 hover:bg-silver-400 text-slate-950 border border-silver-400 font-bold uppercase tracking-wider text-xs px-6 py-4 rounded-sm transition-colors duration-200 cursor-pointer shadow-sm"
                >
                  <Mail className="w-4 h-4 text-amber-600" />
                  <span>Mail: jp@jpstaal.dk</span>
                </a>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="aspect-[4/3] bg-silver-300 border border-silver-400 rounded-sm overflow-hidden shadow-xl">
                <img
                  src="https://assets.auxodev.dk/assets/e3c28b93-c07f-41a2-89fa-cab890739d18/d3a0afd4-2ab6-4677-a8a2-c878fe873b0c.jpg"
                  alt="Muffemontering"
                  className="w-full h-full object-cover filter grayscale hover:grayscale-0 transition-all duration-500"
                />
              </div>
            </div>
          </div>

        </div>

      </div>
    </div>
  )
}
